import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Modal from 'cccisd-modal';
import Tooltip from 'cccisd-tooltip';
import notification from 'cccisd-notification';
import axios from 'cccisd-axios';
import { client as apollo } from 'cccisd-apollo';
import { getFormattedDate } from 'js/helpers';
import TakeAssessment from '../TakeAssessment';
import ContactForm from '../ManageReporters/ContactForm';
import InviteModal from './InviteModal';
import Loader from 'cccisd-loader';
import style from '../style.css';
import { Formik, Form, Field, CccisdSelect } from 'cccisd-formik';
import { gradeOptions } from 'js/assessment/components/DeploymentCenter/EditStudentForm';
import contactNameQuery from './contactName.graphql';
import studentGradeQuery from './studentGrade.graphql';

// Icons
import IconQuill from 'cccisd-icons/quill3';
import IconCopy from 'cccisd-icons/copy3';
import IconCheckmark from 'cccisd-icons/checkmark-circle';
import { IconInProgress } from 'images/icons.js';
import IconStop from 'cccisd-icons/cancel';

const Boilerplate = window.cccisd.boilerplate;

const AssessmentActions = ({ row, setForceReloadQueries, progress, surveyProgress, setSurveyProgress }) => {
    const [lastVisitedDate, setLastVisitedDate] = useState(null);
    const [showContactForm, setShowContactForm] = useState(!row.name);
    const [showGradeForm, setShowGradeForm] = useState(!row.assessment.selectedByPawn?.fields?.grade);
    const [loading, setLoading] = useState(false);
    const studentName = `${row.assessment.selectedByPawn.fields.firstName} ${row.assessment.selectedByPawn.fields.lastName}`;
    const studentPawn = row.assessment.selectedByPawn?.pawn;
    const reporterTypeLower = useMemo(() => row.reporterType?.toLowerCase(), [row.reporterType]);
    const assessmentLink = Boilerplate.url(
        `/sessionPlayer/${row.deploymentId}/${row.assessment.assignmentPlanId}/${row.session.sessionId}/${
            row.session.settings.reporter
        }/${row.pawnId}/${row.pawnHash}?s=${row.session.settings.options.join(',')}`
    );
    const missingInfoRef = useRef();

    useEffect(() => {
        if (progress && progress.length > 0) {
            const sessionProgress = progress.find(
                p => p.pawnId === row.pawnId && p.sessionId === row.session.sessionId
            );
            if (sessionProgress) {
                setLastVisitedDate(sessionProgress.lastVisitedDate);
                const surveyProgressStatus = sessionProgress.surveyProgressStatus;
                if (surveyProgressStatus) {
                    setSurveyProgress(prev => ({
                        ...prev,
                        [`${row.session.sessionId}_${row.pawnId}`]: surveyProgressStatus,
                    }));
                }
            }
        }
    }, [progress, row.pawnId, row.options]);

    const renderStoppedAssessmentList = () => {
        return (
            <div>
                {row.options.map(surveyHandle => {
                    if (
                        surveyProgress &&
                        surveyProgress[`${row.session.sessionId}_${row.pawnId}`] &&
                        surveyProgress[`${row.session.sessionId}_${row.pawnId}`][surveyHandle] === 2
                    ) {
                        return (
                            <div key={surveyHandle}>
                                <span className="text-success">
                                    <IconCheckmark spaceRight />
                                </span>
                                Completed{' '}
                                {getFormattedDate(
                                    surveyProgress[`${row.session.sessionId}_${row.pawnId}`][
                                        surveyHandle + '_lastVisit'
                                    ]
                                )}
                            </div>
                        );
                    }
                    return (
                        <div key={surveyHandle}>
                            <span className="text-danger">
                                <IconStop spaceRight />
                            </span>
                            Stopped {getFormattedDate(row.stopped)}
                        </div>
                    );
                })}
            </div>
        );
    };

    const surveyCompletedCount = useMemo(() => {
        let completedCount = 0;
        for (const surveyHandle of row.options) {
            if (surveyProgress && surveyProgress[`${row.session.sessionId}_${row.pawnId}`]) {
                const surveyStatus = surveyProgress[`${row.session.sessionId}_${row.pawnId}`][surveyHandle];
                if (surveyStatus === 2) {
                    completedCount++;
                }
            }
        }
        return completedCount;
    }, [row.options, surveyProgress, row.session.sessionId, row.pawnId]);

    const checkContactName = async () => {
        let result;
        setLoading(true);
        try {
            result = await apollo.query({
                query: contactNameQuery,
                variables: {
                    pawnId: row.pawnId,
                },
                fetchPolicy: 'network-only',
            });
        } catch (e) {
            throw e;
        } finally {
            setLoading(false);
        }
        const contactName = result?.data?.roles?.contact.fields.contactName;
        setShowContactForm(!contactName);
        return contactName;
    };

    const checkStudentGrade = async () => {
        let result;
        setLoading(true);
        try {
            result = await apollo.query({
                query: studentGradeQuery,
                variables: {
                    planId: row.assessment.assignmentPlanId,
                },
                fetchPolicy: 'network-only',
            });
        } catch (e) {
            throw e;
        } finally {
            setLoading(false);
        }
        const studentGrade = result?.data?.flows?.assignmentPlan?.selectedByPawn.fields.grade;
        setShowGradeForm(!studentGrade);
        return studentGrade;
    };

    const renderProgressStatus = () => {
        return (
            <>
                {surveyCompletedCount > 0 ? (
                    <span>
                        {surveyCompletedCount} of {row.options.length} complete | Last visited:{' '}
                        {getFormattedDate(lastVisitedDate)}
                    </span>
                ) : (
                    <span>In Progress | Last visited: {getFormattedDate(lastVisitedDate)}</span>
                )}
            </>
        );
    };

    const renderContactInfoForm = (callback = () => {}) => {
        return (
            <>
                <div className="alert alert-primary" role="alert">
                    {`No reporter information has been provided. Please take a moment to update the reporter's information.`}
                </div>
                <ContactForm
                    action="edit"
                    contactType={row.session.settings.reporter}
                    closeModal={() => {
                        setShowContactForm(false);
                        if (!showGradeForm) {
                            callback();
                        }
                    }}
                    session={row.session}
                    plan={row.assessment}
                    refreshContactsData={() => {}}
                    refreshPlanData={() => {}}
                    pawn={{ pawnId: row.pawnId, pawnHash: row.pawnHash }}
                    initialValues={{ name: row.name, email: row.email }}
                />
            </>
        );
    };

    const renderStudentGradeForm = (callback = () => {}) => {
        return (
            <>
                <div className="alert alert-primary" role="alert">
                    {`Student grade is required. Please select the student's grade to continue.`}
                </div>
                <Formik
                    initialValues={{
                        grade: row.assessment.selectedByPawn?.fields?.grade || '',
                    }}
                    validate={values => {
                        const errors = {};
                        if (!values.grade) {
                            errors.grade = 'Grade is required';
                        }
                        return errors;
                    }}
                    validateOnChange={false}
                    validateOnBlur={false}
                    onSubmit={async (values, { setSubmitting }) => {
                        if (!studentPawn?.pawnId || !studentPawn?.pawnHash) {
                            setSubmitting(false);
                            return;
                        }

                        try {
                            const response = await axios.put(
                                Boilerplate.route('api.nexus.pawn.update', {
                                    pawn: studentPawn.pawnId,
                                }),
                                {
                                    pawnHash: studentPawn.pawnHash,
                                    grade: values.grade,
                                }
                            );

                            if (response?.data?.status === 'success') {
                                setShowGradeForm(false);
                                callback();
                            } else {
                                // console.error('Failed to update student grade', response);
                            }
                        } catch (error) {
                            console.error(error);
                        }

                        setSubmitting(false);
                    }}
                >
                    {({ errors, isSubmitting }) => (
                        <Form>
                            <label>
                                <b>Grade:</b>
                            </label>
                            <Field name="grade" component={CccisdSelect} options={gradeOptions} />
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    marginTop: '1em',
                                }}
                            >
                                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                    Submit
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </>
        );
    };

    const renderMissingInfoForm = (callback = () => {}) => {
        return (
            <>
                {showContactForm && <>{renderContactInfoForm(callback)}</>}
                {!showContactForm && showGradeForm && renderStudentGradeForm(callback)}
            </>
        );
    };

    const handleCopyLink = async () => {
        try {
            const studentGrade = await checkStudentGrade();
            const studentGradeMissing = !studentGrade;
            const isStudentReporter = reporterTypeLower === 'student';

            let contactNameMissing = false;
            if (!isStudentReporter) {
                const contactName = await checkContactName();
                contactNameMissing = !contactName;
            } else {
                setShowContactForm(false);
            }

            if (contactNameMissing || studentGradeMissing) {
                if (missingInfoRef.current) {
                    missingInfoRef.current.open();
                }
                return;
            }

            await copyLink();
        } catch (error) {
            console.error(error);
        }
    };

    const copyLink = () => {
        return navigator.clipboard.writeText(assessmentLink).then(
            () => {
                notification({
                    message: 'Copied URL to clipboard',
                    type: 'success',
                });
            },
            err => {
                notification({
                    message: 'Failed to copy URL to clipboard',
                    type: 'error',
                });
            }
        );
    };

    if (row.session.completedDate || surveyCompletedCount === row.options.length) {
        const lastSurveyHandle = row.options[row.options.length - 1];
        /* eslint-disable-next-line */
        const lastSurveyCompletedDate = surveyProgress
            ? surveyProgress[`${row.session.sessionId}_${row.pawnId}`]
                ? surveyProgress[`${row.session.sessionId}_${row.pawnId}`][lastSurveyHandle + '_lastVisit']
                : null
            : null;
        return (
            <>
                <span className="text-success">
                    <IconCheckmark spaceRight />
                </span>
                Completed {row.session.completedDate || getFormattedDate(lastSurveyCompletedDate)}
            </>
        );
    }

    return (
        <div className={style.assessmentActionsWrapper}>
            {row.stopped ? (
                <>{renderStoppedAssessmentList()}</>
            ) : (
                <div>
                    <div className={style.assessmentActions}>
                        <Modal
                            title={
                                showContactForm || showGradeForm
                                    ? `Update information for ${studentName}`
                                    : 'Take Assessment'
                            }
                            trigger={
                                <Tooltip
                                    title={`${
                                        row.name
                                            ? `Begin assessment(s) for ${row.name}.`
                                            : `Begin assessment(s) for this ${reporterTypeLower || 'reporter'}.`
                                    }`}
                                >
                                    <button className="btn btn-primary btn-sm" type="button">
                                        <IconQuill /> Take Now
                                    </button>
                                </Tooltip>
                            }
                            beforeShow={() => {
                                if (row.reporterType !== 'Student') {
                                    checkContactName();
                                }
                                checkStudentGrade();
                            }}
                            size="large"
                            render={({ closeModal }) => {
                                if (loading) {
                                    return <Loader loading type="inline" />;
                                }
                                if (showContactForm || showGradeForm) {
                                    return renderMissingInfoForm();
                                }
                                return (
                                    <TakeAssessment
                                        session={row.session}
                                        reporterName={row.name}
                                        assessmentLink={assessmentLink}
                                        closeModal={closeModal}
                                        studentName={studentName}
                                    />
                                );
                            }}
                        />
                        <InviteModal
                            key={row.pawnId}
                            row={row}
                            assessmentLink={assessmentLink}
                            checkContactName={checkContactName}
                            checkStudentGrade={checkStudentGrade}
                            renderStudentGradeForm={renderStudentGradeForm}
                            loading={loading}
                        />
                        <Tooltip
                            title={`Copy the link to send the assessment(s) to ${
                                row.name ?? `this ${reporterTypeLower || 'reporter'}`
                            }.`}
                        >
                            <Modal
                                trigger={null}
                                size="large"
                                ref={missingInfoRef}
                                title="Update information"
                                beforeClose={() => {
                                    // props.history.push(`${props.location.pathname}`);
                                }}
                                render={({ closeModal }) => {
                                    return renderMissingInfoForm(() =>
                                        copyLink().then(() => {
                                            closeModal();
                                        })
                                    );
                                }}
                            />
                            <button className="btn btn-secondary btn-sm" type="button" onClick={handleCopyLink}>
                                <IconCopy /> Copy Link
                            </button>
                            {/* {showContactForm || showGradeForm ? (
                                <Modal
                                    title={`Update information for ${studentName}`}
                                    trigger={
                                        <button className="btn btn-secondary btn-sm" type="button">
                                            <IconCopy /> Copy Link
                                        </button>
                                    }
                                    size="large"
                                    beforeShow={() => {
                                        if (row.reporterType !== 'Student') {
                                            checkContactName();
                                        }
                                        checkStudentGrade();
                                    }}
                                    render={({ closeModal }) => {
                                        if (loading) {
                                            return <Loader loading type="inline" />;
                                        }
                                        if (showContactForm || showGradeForm) {
                                            return renderMissingInfoForm(() =>
                                                handleCopyLink().then(() => {
                                                    closeModal();
                                                })
                                            );
                                        }
                                        return handleCopyLink().then(() => {
                                            closeModal();
                                        });
                                    }}
                                />
                            ) : (
                                <button className="btn btn-secondary btn-sm" type="button" onClick={handleCopyLink}>
                                    <IconCopy /> Copy Link
                                </button>
                            )} */}
                        </Tooltip>
                    </div>
                    <div className={style.assessmentActionsProgress}>
                        {lastVisitedDate && (
                            <span style={{ fontSize: 'smaller', fontWeight: '300' }}>
                                <span style={{ color: '#154988', marginRight: '0.3em' }}>
                                    <IconInProgress />
                                </span>
                                {renderProgressStatus()}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssessmentActions;

AssessmentActions.propTypes = {
    row: PropTypes.object.isRequired,
    setForceReloadQueries: PropTypes.func.isRequired,
    progress: PropTypes.array.isRequired,
    surveyProgress: PropTypes.object,
    setSurveyProgress: PropTypes.func.isRequired,
};
AssessmentActions.defaultProps = {};
